/**
 * Accordion web component
 * Handles toggling of accordion panels, ARIA attributes, and inert state.
 */
class Accordion extends HTMLElement {
    /**
     * Initializes the accordion component, sets up click event listeners on headings.
     */
    constructor() {
        super();
        var elements = this.querySelectorAll('.accordion__list-item-heading');
        this.mobileOnly = this.classList.contains('accordion--mobile-only');

        elements.forEach((element) => {
            element.addEventListener('click', (event) => {
                event.preventDefault();
                const wrapper = event.target.closest('.accordion__list-item');
                const showOne = event.target.closest('.accordion').classList.contains('accordion--show-one-row');
                if (wrapper) {
                    this.toggle(wrapper, showOne);
                }
            });
        });

        // Handle inert on mobile-only accordions
        if (this.mobileOnly) {
            this.handleMobileOnlyInert();
            // Debounce resize handler with 150ms delay
            const debouncedHandler = debounce(() => this.handleMobileOnlyInert(), 150);
            window.addEventListener('resize', debouncedHandler);
        }
    }

    /**
     * Handles inert attribute for mobile-only accordions on desktop
     * Removes inert from all description elements when on desktop
     */
    handleMobileOnlyInert() {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        const descriptions = this.querySelectorAll('.accordion__list-item-description');

        descriptions.forEach((desc) => {
            if (isDesktop) {
                // On desktop, remove inert so content is accessible
                desc.removeAttribute('inert');
            } else {
                // On mobile, only inactive items should have inert
                const wrapper = desc.closest('.accordion__list-item');
                if (!wrapper.classList.contains('active')) {
                    desc.setAttribute('inert', '');
                }
            }
        });
    }

    /**
     * Closes all accordion items in this component.
     */
    closeAll() {
        const items = this.querySelectorAll('.accordion__list-item.active');
        items.forEach((item) => {
            item.classList.remove('active');
            const button = item.querySelector('.accordion__list-item-heading');
            const desc = item.querySelector('.accordion__list-item-description');
            if (button) button.setAttribute('aria-expanded', 'false');
            if (desc) {
                desc.setAttribute('aria-hidden', 'true');
                // Only set inert if not on desktop (for mobile-only accordions)
                if (!this.mobileOnly || !window.matchMedia('(min-width: 1024px)').matches) {
                    desc.setAttribute('inert', '');
                }
            }
        });
    }

    /**
     * Toggles the open/closed state of an accordion item.
     *
     * @param {Element} wrapper - The .accordion__list-item element to toggle.
     * @param {boolean} showOne - If true, only one item can be open at a time.
     */
    toggle(wrapper, showOne) {
        const button = wrapper.querySelector('.accordion__list-item-heading');
        const desc = wrapper.querySelector('.accordion__list-item-description');
        if (wrapper.classList.contains('active')) {
            wrapper.classList.remove('active');
            if (button) button.setAttribute('aria-expanded', 'false');
            if (desc) {
                desc.setAttribute('aria-hidden', 'true');
                // Only set inert if not on desktop (for mobile-only accordions)
                if (!this.mobileOnly || !window.matchMedia('(min-width: 1024px)').matches) {
                    desc.setAttribute('inert', '');
                }
            }
        } else {
            var allItems = this.querySelectorAll('.accordion__list-item.active');
            if (showOne && allItems.length > 0) {
                allItems.forEach((item) => {
                    item.classList.remove('active');
                    const btn = item.querySelector('.accordion__list-item-heading');
                    const d = item.querySelector('.accordion__list-item-description');
                    if (btn) btn.setAttribute('aria-expanded', 'false');
                    if (d) {
                        d.setAttribute('aria-hidden', 'true');
                        // Only set inert if not on desktop (for mobile-only accordions)
                        if (!this.mobileOnly || !window.matchMedia('(min-width: 1024px)').matches) {
                            d.setAttribute('inert', '');
                        }
                    }
                });
            }
            wrapper.classList.add('active');
            if (button) button.setAttribute('aria-expanded', 'true');
            if (desc) {
                desc.setAttribute('aria-hidden', 'false');
                desc.removeAttribute('inert');
            }
        }
    }
}

if (!customElements.get('accordion-component')) {
    customElements.define('accordion-component', Accordion);
}
